/**
 * Send Referral Page - Trade Loop v2.0
 *
 * Wizard-style referral creation flow
 *
 * Steps:
 * 1. Select Partner(s) - Choose who to send the referral to
 * 2. Client Information - Enter client details
 * 3. Job Details - Describe the job/project
 * 4. Review & Send - Confirm and send
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { compressImage, isCompressibleImage } from "@/lib/imageUtils";
import { classifyMediaFile, MEDIA_ACCEPT_ATTRIBUTE } from "@/lib/mediaUploadValidation";
import { getSubmitGate, useBackgroundAttachmentUploads, type NewAttachment } from "@/hooks/useBackgroundAttachmentUploads";
import { isNative } from "@/utils/platform";
import { cn } from "@/lib/utils";
import { captureImageInto, isNativeCaptureAvailable } from "@/services/mediaCapture";
import { useTradeSections, TradeSection } from "@/hooks/useTrades";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useShare } from "@/hooks/useShare";
import { UpgradePrompt } from "@/components/subscription";
import AppLayout from "@/components/layout/AppLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ChevronDown } from "lucide-react";
import {
  Send,
  User,
  Building2,
  MapPin,
  DollarSign,
  FileText,
  Phone,
  Mail,
  Users,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Upload,
  X,
  Paperclip,
  ChevronRight,
  ChevronLeft,
  Check,
  ClipboardList,
  Clock,
  AlertTriangle,
  Zap,
  Share2,
  Copy,
  Link as LinkIcon,
  Info,
  ImagePlus,
  Camera,
  Loader2,
  RotateCw,
  Video,
  Search,
} from "lucide-react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/ui/avatar";
import CityStateAutocomplete from "@/components/ui/CityStateAutocomplete";
import StreetAddressAutocomplete, { SelectedAddress } from "@/components/ui/StreetAddressAutocomplete";
import { LocationResult } from "@/lib/location-data";
import { isValidEmail, isValidPhone } from "@/utils/validation";

interface Partner {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
  trade?: string;
  profileImageUrl?: string;
}

interface ReferralFormData {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  jobDescription: string;
  streetAddress: string;
  cityState: string;
}

// Wizard step configuration
const STEPS = [
  { id: 1, title: "Client Info", icon: User },
  { id: 2, title: "Job Details", icon: FileText },
  { id: 3, title: "Select Partner", icon: Users },
  { id: 4, title: "Review", icon: ClipboardList },
];

export default function SendReferralPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();
  const { share: shareNative, canShare } = useShare();
  const { isAtLimit, canPerformAction, plan, featureFlags } = useSubscription();

  // Check if user is an organization member (not admin/owner)
  const isOrgMember = user?.organizationMembership?.role === 'member';

  // Fetch trades from database (grouped by section)
  const { data: tradeSections = [], isLoading: tradesLoading } = useTradeSections();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const wizardTopRef = useRef<HTMLDivElement>(null);

  const isInitialStepMount = useRef(true);
  useEffect(() => {
    if (isInitialStepMount.current) {
      isInitialStepMount.current = false;
      return;
    }
    // rAF defers past the new step's autoFocus scroll-into-view so it doesn't fight us.
    const raf = requestAnimationFrame(() => {
      const scrollContainer = wizardTopRef.current?.closest("main");
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: "auto" });
      } else {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [currentStep]);

  // Get partner IDs from URL params
  const partnerIdParam = searchParams.get("partnerId");
  const partnerIdsParam = searchParams.get("partnerIds");
  const tradeParam = searchParams.get("trade");

  // Parse partner IDs
  const preSelectedPartnerIds = useMemo(() => {
    if (partnerIdParam) return [partnerIdParam];
    if (partnerIdsParam) return partnerIdsParam.split(",");
    return [];
  }, [partnerIdParam, partnerIdsParam]);

  // Form state
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | undefined>(undefined);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<string[]>([]);
  const [sendToMultiple, setSendToMultiple] = useState(false);
  const [partnerSearchQuery, setPartnerSearchQuery] = useState("");

  const [formData, setFormData] = useState<ReferralFormData>({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    jobDescription: "",
    streetAddress: "",
    cityState: "",
  });
  const [cityStateInput, setCityStateInput] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<{ city: string; state: string } | null>(null);

  // File attachments: each upload runs in the background while the user keeps going. Only the
  // finished ones are sent with the referral; submitting waits for the rest (see handleSubmitClick).
  const uploads = useBackgroundAttachmentUploads();
  const attachments = uploads.completed;
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Send was pressed while uploads were still running — submit once they finish.
  const [submitWhenUploadsFinish, setSubmitWhenUploadsFinish] = useState(false);

  // Trade state (single-select)
  const [selectedTrade, setSelectedTrade] = useState<string | null>(null);
  const [tradePopoverOpen, setTradePopoverOpen] = useState(false);

  // Custom trade state (for "Other" job type option) — single custom value
  const [showCustomTradeInput, setShowCustomTradeInput] = useState(false);
  const [customTrade, setCustomTrade] = useState<string>('');

  // Urgency level state
  const [urgencyLevel, setUrgencyLevel] = useState<'low' | 'normal' | 'urgent'>('normal');

  // Share Link state
  const [useShareLink, setUseShareLink] = useState(false);
  const [invitedPerson, setInvitedPerson] = useState({
    name: ''
  });
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  // Availability Request state (Pro/Grow feature)
  const [requestAvailabilityFirst, setRequestAvailabilityFirst] = useState(false);
  const [availabilityDescription, setAvailabilityDescription] = useState('');
  const [availabilityWindow, setAvailabilityWindow] = useState<'2' | '24'>('2');

  // Tracks which step-3 method accordion panel is open. Decoupled from the
  // method flags so the user can visually collapse a panel (empty string)
  // without silently switching methods.
  const [expandedMethod, setExpandedMethod] = useState<string>("partner");

  // Consent checkbox state (required before sending)
  const [hasCustomerConsent, setHasCustomerConsent] = useState(false);

  // Sync sendToMultiple with requestAvailabilityFirst
  useEffect(() => {
    setSendToMultiple(requestAvailabilityFirst);
  }, [requestAvailabilityFirst]);

  // Availability-request mode requires Pro/Grow. If the user lacks the feature
  // (e.g. plan downgrade or pre-selected partners pushed them into the mode),
  // fall back to the standard partner-selection flow.
  useEffect(() => {
    if (!featureFlags.availabilityRequests && requestAvailabilityFirst) {
      setRequestAvailabilityFirst(false);
      setSelectedPartnerIds([]);
      setExpandedMethod("partner");
    }
  }, [featureFlags.availabilityRequests, requestAvailabilityFirst]);

  // Select a trade (single-select). Choosing a predefined trade clears the
  // "Other" custom flow; choosing "Other" clears any predefined selection.
  const selectTrade = (trade: string) => {
    if (trade === '__CUSTOM__') {
      const enabling = !showCustomTradeInput;
      setShowCustomTradeInput(enabling);
      if (enabling) {
        setSelectedTrade(null);
      } else {
        setCustomTrade('');
      }
      setTradePopoverOpen(false);
      return;
    }
    setSelectedTrade(trade);
    setShowCustomTradeInput(false);
    setCustomTrade('');
    setTradePopoverOpen(false);
  };

  const clearTradeSelection = () => {
    setSelectedTrade(null);
  };

  const clearCustomTrade = () => {
    setShowCustomTradeInput(false);
    setCustomTrade('');
  };

  // Fetch connections (partners)
  const { data: connections = [], isPending: connectionsLoading } = useQuery({
    queryKey: ["/api/workflows/connections"],
    queryFn: () => api.get("/api/workflows/connections"),
    enabled: !!user,
  });

  // Initialize selected partners from URL params
  useEffect(() => {
    if (preSelectedPartnerIds.length > 0) {
      if (preSelectedPartnerIds.length === 1) {
        setSelectedPartnerId(preSelectedPartnerIds[0]);
        setExpandedMethod("partner");
      } else {
        // Multiple partners: set availability request mode
        setSelectedPartnerIds(preSelectedPartnerIds);
        setRequestAvailabilityFirst(true);
        setExpandedMethod("availability");
      }
    }
  }, [preSelectedPartnerIds]);

  // Get partners array safely
  const partnersArray: Partner[] = useMemo(() => {
    return Array.isArray(connections) ? connections : [];
  }, [connections]);

  // Filter partners based on search query
  const filteredPartnersArray: Partner[] = useMemo(() => {
    if (!partnerSearchQuery.trim()) {
      return partnersArray;
    }

    const query = partnerSearchQuery.toLowerCase();
    return partnersArray.filter((partner) => {
      const fullName = `${partner.firstName} ${partner.lastName}`.toLowerCase();
      const company = partner.company?.toLowerCase() || "";
      const trade = partner.trade?.toLowerCase() || "";

      return fullName.includes(query) || company.includes(query) || trade.includes(query);
    });
  }, [partnersArray, partnerSearchQuery]);

  // Auto-select first partner (organization admin) for org members
  useEffect(() => {
    if (isOrgMember && partnersArray.length > 0 && !selectedPartnerId) {
      setSelectedPartnerId(partnersArray[0].id);
      setSendToMultiple(false);
    }
  }, [isOrgMember, partnersArray, selectedPartnerId]);

  // Get selected partner details
  const selectedPartner = useMemo(() => {
    if (!selectedPartnerId) return null;
    return partnersArray.find((p) => p.id === selectedPartnerId);
  }, [partnersArray, selectedPartnerId]);

  // Get multiple selected partners
  const selectedPartners = useMemo(() => {
    return partnersArray.filter((p) => selectedPartnerIds.includes(p.id));
  }, [partnersArray, selectedPartnerIds]);

  // Create referral mutation (single partner)
  const createReferralMutation = useMutation({
    mutationFn: async (data: {
      receiverId: string;
      clientName: string;
      clientEmail?: string;
      clientPhone?: string;
      jobDescription: string;
      jobLocation: string;
      attachments?: any;
      trades?: string[];
      customTrades?: string[];
      urgencyLevel?: 'low' | 'normal' | 'urgent';
    }) => {
      return api.post("/api/workflows/referral/create", data);
    },
  });


  // Handle file upload (supports multiple files, max 20)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    // Reset now so the same file can be picked again (e.g. after removing it).
    e.target.value = "";
    void uploadSelectedFiles(picked);
  };

  // Shared by the web file input and the native camera — both hand over plain Files.
  // Each valid file becomes a tile at once and uploads in the background.
  const uploadSelectedFiles = async (files: File[]) => {
    const MAX_FILES = 20;
    // In-flight and failed tiles hold a slot too.
    const currentCount = uploads.items.length;
    const availableSlots = MAX_FILES - currentCount;

    if (availableSlots <= 0) {
      showError(
        "Maximum Files Reached",
        `You can only upload up to ${MAX_FILES} files. Please remove some files first.`
      );
      return;
    }

    // Limit the number of files to process
    const filesToProcess = files.slice(0, availableSlots);

    if (files.length > availableSlots) {
      showWarning(
        "Some Files Skipped",
        `Only ${availableSlots} file(s) can be added. ${files.length - availableSlots} file(s) were skipped.`
      );
    }

    // Classify files by MIME type, falling back to file extension when the
    // browser fails to identify the type (common for .mov on Windows/Linux).
    const validFiles: NewAttachment[] = [];
    const invalidFiles: string[] = [];

    for (const rawFile of filesToProcess) {
      const { kind, file } = classifyMediaFile(rawFile);

      if (kind === 'unknown') {
        invalidFiles.push(`${rawFile.name} (invalid type)`);
        continue;
      }

      const isVideo = kind === 'video';

      // Validate file size (25MB for images, 250MB for videos)
      const maxSize = isVideo ? 250 * 1024 * 1024 : 25 * 1024 * 1024;
      const maxSizeLabel = isVideo ? "250MB" : "25MB";

      if (file.size > maxSize) {
        // Auto-compress images over the limit — as part of the background upload,
        // so the tile still appears straight away.
        if (!isVideo && isCompressibleImage(file)) {
          validFiles.push({
            file,
            kind,
            // Compress only far enough to fit the 25MB cap, not down to it.
            prepare: (f) => compressImage(f, 24, 0.85),
          });
          continue;
        }
        invalidFiles.push(`${file.name} (exceeds ${maxSizeLabel})`);
        continue;
      }

      validFiles.push({ file, kind });
    }

    if (invalidFiles.length > 0) {
      showError(
        "Some Files Invalid",
        `Skipped: ${invalidFiles.join(", ")}`
      );
    }

    if (validFiles.length === 0) return;

    // Each tile shows its own progress; success needs no toast. Failures get one
    // toast per batch (the tiles keep the reason and a Retry button).
    const outcomes = await uploads.addFiles(validFiles);
    const failed = outcomes.filter((o) => o.status === 'failed');
    if (failed.length > 0) {
      const suffix = failed.length > 1 ? ` (${failed.length} files failed)` : '';
      showError('Upload Failed', `${failed[0].error}${suffix}`);
    }
  };

  // Generate invitation link
  const handleGenerateLink = async () => {
    // Validate form data first
    if (!formData.clientName.trim()) {
      showError("Client Name Required", "Please enter the client's name before generating a link.");
      return;
    }
    if (!formData.jobDescription.trim()) {
      showError("Job Description Required", "Please describe the job before generating a link.");
      return;
    }
    if (!formData.cityState.trim()) {
      showError("City/State Required", "Please select a city and state before generating a link.");
      return;
    }

    setIsGeneratingLink(true);

    try {
      // Combine street address (if provided) and city/state
      const fullJobLocation = formData.streetAddress.trim()
        ? `${formData.streetAddress.trim()}, ${formData.cityState.trim()}`
        : formData.cityState.trim();

      const response = await api.post('/api/workflows/referral/create-invitation-link', {
        clientName: formData.clientName.trim(),
        clientEmail: formData.clientEmail.trim() || undefined,
        clientPhone: formData.clientPhone.trim() || undefined,
        jobDescription: formData.jobDescription.trim(),
        jobLocation: fullJobLocation,
        attachments: attachments.length > 0 ? attachments : undefined,
        trades: selectedTrade ? [selectedTrade] : undefined,
        urgencyLevel,
        invitedName: invitedPerson.name.trim() || undefined
      });

      if (response?.data?.invitationLink) {
        setGeneratedLink(response.data.invitationLink);
        showSuccess('Link Generated!', 'Your referral invitation link is ready to share.');
      }
    } catch (error: any) {
      showError('Failed to Generate Link', error.message || 'Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      showSuccess('Link Copied!', 'The invitation link has been copied to your clipboard.');
    } catch (error) {
      showError('Copy Failed', 'Please copy the link manually.');
    }
  };

  // Native mobile share using Capacitor. Accepts an optional linkOverride so
  // callers can trigger a share immediately after creating the invitation link,
  // before the setGeneratedLink state update has flushed — this also preserves
  // the click's user-gesture chain that the Web Share API requires.
  const handleNativeShare = async (linkOverride?: string) => {
    const linkToShare = linkOverride ?? generatedLink;
    if (!linkToShare) return;

    // Check if sharing is supported
    if (!canShare) {
      console.log('Share not supported, falling back to copy');
      try {
        await navigator.clipboard.writeText(linkToShare);
        showSuccess('Link Copied', 'Paste it in your messaging app to share.');
      } catch {
        showError('Copy Failed', 'Please copy the link manually.');
      }
      return;
    }

    try {
      console.log('Attempting to share via Capacitor/Web Share API:', { linkToShare });

      const inviterName =
        [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
        user?.fullName?.trim() ||
        'A friend';

      const recipientName = invitedPerson.name.trim();
      const greeting = recipientName ? `Hi ${recipientName}! ` : '';

      const shareData = {
        title: 'Trade Loop Referral',
        text: `${greeting}${inviterName} has a referral opportunity for you! Client: ${formData.clientName}\n\nJob: ${formData.jobDescription}\n\nClick to claim:`,
        url: linkToShare,
        dialogTitle: 'Share Referral Link'
      };

      const success = await shareNative(shareData);

      if (success) {
        console.log('Share successful');
        showSuccess('Shared!', 'Invitation link shared successfully.');
      } else {
        console.log('Share cancelled by user');
        // User cancelled - referral remains created; manual "Share Link"
        // button stays visible for them to retry.
      }
    } catch (error: any) {
      console.error('Share error:', error);
      showError('Share Failed', 'Please try copying the link instead.');
      // Fallback to copy
      try {
        await navigator.clipboard.writeText(linkToShare);
      } catch {
        // Clipboard write failed too — manual share button still works.
      }
    }
  };

  // Validation for each step
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        // Client Info validation
        if (!formData.clientName.trim()) {
          showError("Client Name Required", "Please enter the client's name.");
          return false;
        }
        // Email and phone are optional, but if provided they must be valid
        if (formData.clientEmail.trim() && !isValidEmail(formData.clientEmail.trim())) {
          showError("Invalid Email", "Please enter a valid email address or leave it blank.");
          return false;
        }
        if (formData.clientPhone.trim() && !isValidPhone(formData.clientPhone)) {
          showError("Invalid Phone", "Please enter a valid 10-digit phone number or leave it blank.");
          return false;
        }
        return true;
      case 2:
        // Job Details validation
        if (!formData.jobDescription.trim()) {
          showError("Job Description Required", "Please describe the job.");
          return false;
        }
        if (!formData.cityState.trim()) {
          showError("City/State Required", "Please select a city and state.");
          return false;
        }
        return true;
      case 3:
        // Partner Selection validation - skip if using share link
        if (useShareLink) {
          return true; // Share link mode doesn't need partner selection
        }
        const hasPartner = sendToMultiple
          ? selectedPartnerIds.length > 0
          : !!selectedPartnerId;
        if (!hasPartner) {
          showError("Partner Required", "Please select at least one partner or use Share Link.");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  // Navigate to next step
  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  // Navigate to previous step
  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Handle form submission. `afterUploads` means Send was pressed earlier and we are
  // submitting now that the background uploads have finished.
  const handleSubmit = async ({ afterUploads = false }: { afterUploads?: boolean } = {}) => {
    // Combine street address (if provided) and city/state into full address
    const fullJobLocation = formData.streetAddress.trim()
      ? `${formData.streetAddress.trim()}, ${formData.cityState.trim()}`
      : formData.cityState.trim();

    try {
      // If using share link, create referral with invitation link
      if (useShareLink) {
        setIsGeneratingLink(true);

        // Get non-empty custom trade (single value)
        const trimmedCustomTrade = showCustomTradeInput ? customTrade.trim() : '';

        const response = await api.post('/api/workflows/referral/create-invitation-link', {
          clientName: formData.clientName.trim(),
          clientEmail: formData.clientEmail.trim() || undefined,
          clientPhone: formData.clientPhone.trim() || undefined,
          jobDescription: formData.jobDescription.trim(),
          jobLocation: fullJobLocation,
          attachments: attachments.length > 0 ? attachments : undefined,
          trades: selectedTrade ? [selectedTrade] : undefined,
          customTrades: trimmedCustomTrade ? [trimmedCustomTrade] : undefined,
          urgencyLevel,
          invitedName: invitedPerson.name.trim() || undefined,
          userConsentToShareClientInfo: hasCustomerConsent
        });

        if (response?.data?.invitationLink) {
          const newLink = response.data.invitationLink;
          setGeneratedLink(newLink);
          setIsGeneratingLink(false);
          showSuccess(
            'Referral Created!',
            'Your invitation link is ready to share below.'
          );
          // Auto-invoke the share sheet immediately, preserving the click's
          // user-gesture chain (required by the Web Share API on Safari/Chrome).
          // Pass the link explicitly because setGeneratedLink hasn't flushed yet.
          // Share failures fall back to copy inside handleNativeShare; the
          // referral itself remains created either way.
          // After waiting on uploads the click's gesture has expired, so the web
          // share sheet would be refused — leave it to the Share Link button.
          // The native share plugin needs no gesture.
          if (!afterUploads || isNative) {
            await handleNativeShare(newLink);
          }
          // Don't navigate - stay on Step 4 to show sharing UI so the user can
          // retry sharing manually if they cancelled or share failed.
        }
        return;
      }

      const partnerIds = sendToMultiple ? selectedPartnerIds : [selectedPartnerId];

      // Branch on user intent, not partner count: if "Request Availability First"
      // is on, always go through the availability-request flow (even for a single
      // partner). Otherwise send a direct referral.
      if (requestAvailabilityFirst) {
        // Map urgency level to integer (1-4)
        const urgencyLevelMap: Record<string, number> = {
          'low': 1,
          'normal': 2,
          'high': 3,
          'urgent': 4
        };

        // Calculate expiration timestamp based on selected availability window
        const hoursToAdd = parseInt(availabilityWindow); // '2' or '24' hours
        const expirationDate = new Date();
        expirationDate.setHours(expirationDate.getHours() + hoursToAdd);
        const expiresAtISO = expirationDate.toISOString();

        // Create availability request (includes client info for the eventual referral)
        const availabilityRequestData = {
          recipientIds: partnerIds,
          jobDescription: formData.jobDescription.trim(),
          jobLocation: fullJobLocation,
          urgencyLevelId: urgencyLevelMap[urgencyLevel],
          tradeIds: selectedTrade ? [Number(selectedTrade)].filter(id => !isNaN(id)) : undefined,
          expiresAt: expiresAtISO, // Calculated from availabilityWindow (2 or 24 hours)
          // Client information (required for backend)
          clientName: formData.clientName.trim(),
          clientEmail: formData.clientEmail.trim() || undefined,
          clientPhone: formData.clientPhone.trim() || undefined,
          referralAttachments: attachments.length > 0 ? attachments : undefined,
          userConsentToShareClientInfo: hasCustomerConsent,
        };

        await api.post("/api/workflows/availability-request/create", availabilityRequestData);

        showSuccess(
          "Availability Request Sent!",
          `Your availability request was sent to ${partnerIds.length} partner${partnerIds.length === 1 ? '' : 's'}. You'll be notified when they respond.`
        );

        // Invalidate + refetch the panel's queries so the Sent tab updates
        // immediately on /referrals without waiting for staleTime or realtime.
        // Key must match AvailabilityRequestsPanel: ['availability-requests', ...]
        await queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
        await queryClient.refetchQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
        navigate("/referrals");
      } else {
        // Single partner - use existing endpoint
        const trimmedCustomTrade = showCustomTradeInput ? customTrade.trim() : '';

        const referralData = {
          clientName: formData.clientName.trim(),
          clientEmail: formData.clientEmail.trim() || undefined,
          clientPhone: formData.clientPhone.trim() || undefined,
          jobDescription: formData.jobDescription.trim(),
          jobLocation: fullJobLocation,
          attachments: attachments.length > 0 ? attachments : undefined,
          trades: selectedTrade ? [selectedTrade] : undefined,
          customTrades: trimmedCustomTrade ? [trimmedCustomTrade] : undefined,
          urgencyLevel,
          receiverId: partnerIds[0],
          userConsentToShareClientInfo: hasCustomerConsent,
        };

        await createReferralMutation.mutateAsync(referralData);

        showSuccess(
          "Referral Sent!",
          "Your referral has been sent successfully."
        );

        // Refetch referral list cache before navigating
        await queryClient.refetchQueries({ queryKey: ["/api/workflows/referral/list"] });
        navigate("/referrals");
      }
    } catch (error: any) {
      showError(
        "Failed to Send",
        error.message || "Could not send the referral. Please try again."
      );
    }
  };

  // Send/Create button. Uploads still running are never dropped: we wait for them and
  // submit automatically (the effect below). Failed uploads must be retried or removed first.
  const handleSubmitClick = () => {
    const gate = getSubmitGate(uploads.summary);
    if (gate === 'failed') {
      showUploadsFailedError();
    } else if (gate === 'waiting') {
      setSubmitWhenUploadsFinish(true);
    } else {
      void handleSubmit();
    }
  };

  const showUploadsFailedError = () =>
    showError(
      "Some Files Didn't Upload",
      "Retry or remove the files marked as failed, then send again."
    );

  // Set when the pending send is taken (or called off). The state flag alone cannot guard it:
  // handleSubmit starts the create mutation, whose re-render can commit before the queued
  // `setSubmitWhenUploadsFinish(false)` is applied — this effect then still sees `true` and sent
  // the referral a second time.
  const pendingSendTakenRef = useRef(false);

  // No dependency list: it re-checks after every render and does nothing unless a send is pending.
  useEffect(() => {
    if (!submitWhenUploadsFinish) {
      pendingSendTakenRef.current = false;
      return;
    }
    if (pendingSendTakenRef.current) return;
    // Going back a step or unticking consent calls off the pending send.
    if (currentStep !== STEPS.length || !hasCustomerConsent) {
      pendingSendTakenRef.current = true;
      setSubmitWhenUploadsFinish(false);
      return;
    }
    const gate = getSubmitGate(uploads.summary);
    if (gate === 'waiting') return;
    pendingSendTakenRef.current = true;
    setSubmitWhenUploadsFinish(false);
    if (gate === 'failed') {
      showUploadsFailedError();
    } else {
      void handleSubmit({ afterUploads: true });
    }
  });

  // Format phone number as user types
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  // Handle input changes
  const handleInputChange = (field: keyof ReferralFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle phone number change with formatting
  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData((prev) => ({ ...prev, clientPhone: formatted }));
  };

  // Toggle partner selection for multiple mode
  const togglePartnerSelection = (partnerId: string) => {
    setSelectedPartnerIds((prev) =>
      prev.includes(partnerId)
        ? prev.filter((id) => id !== partnerId)
        : [...prev, partnerId]
    );
  };

  // Step Progress Indicator
  const StepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-start justify-between">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;

          return (
            <div key={step.id} className="flex items-start flex-1">
              {/* Step circle and label */}
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                    isCompleted
                      ? "bg-green-500 text-white"
                      : isCurrent
                      ? "bg-blue-500 text-white ring-4 ring-blue-100"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <StepIcon className="w-5 h-5" />
                  )}
                </div>
                <span
                  className={`mt-2 text-xs font-medium text-center leading-tight min-h-[2.5rem] flex items-start justify-center ${
                    isCurrent ? "text-blue-600" : isCompleted ? "text-green-600" : "text-gray-500"
                  }`}
                >
                  {step.title}
                </span>
              </div>

              {/* Connector line - positioned at icon height */}
              {index < STEPS.length - 1 && (
                <div className="flex items-center pt-5 flex-1 min-w-[1rem]">
                  <div
                    className={`w-full h-1 rounded ${
                      currentStep > step.id ? "bg-green-500" : "bg-gray-200"
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Step 1: Method selection (choose how to send the referral)
  const renderStep1 = () => {
    // Org members can only send through their organization admin — no method
    // choice. Skip the accordion and render the disabled partner picker.
    if (isOrgMember) {
      return (
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>Select Partner</div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {connectionsLoading ? (
              <div className="text-center py-4 text-gray-500">Loading partners...</div>
            ) : partnersArray.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No partners yet</p>
                <p className="text-sm mt-1">Connect with partners in your network first.</p>
                <Button type="button" variant="outline" className="mt-4" onClick={() => navigate("/network")}>
                  Find Partners
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Select
                  value={selectedPartnerId}
                  onValueChange={(value) => setSelectedPartnerId(value)}
                  disabled
                >
                  <SelectTrigger className="w-full" disabled>
                    <SelectValue placeholder="Select a partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partnersArray.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        <div className="flex items-center gap-2">
                          <span>
                            {partner.firstName} {partner.lastName}
                          </span>
                          <span className="text-gray-400">(Organization Admin)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedPartner && (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Avatar className="w-12 h-12 border-2 border-white shadow">
                      <AvatarImage src={selectedPartner.profileImageUrl || undefined} alt={`${selectedPartner.firstName} ${selectedPartner.lastName}`} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                        {selectedPartner.firstName?.[0]}{selectedPartner.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {selectedPartner.firstName} {selectedPartner.lastName}
                      </p>
                      {selectedPartner.company && (
                        <p className="text-sm text-gray-600">{selectedPartner.company}</p>
                      )}
                    </div>
                    {selectedPartner.trade && (
                      <Badge className="ml-auto bg-blue-100 text-blue-700">
                        {selectedPartner.trade}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    const canRequestAvailability = featureFlags.availabilityRequests;

    const handleMethodChange = (value: string) => {
      // Empty string: user re-clicked the open trigger to collapse. Preserve
      // selections and method flags so re-expanding restores their prior work.
      if (!value) {
        setExpandedMethod("");
        return;
      }
      // Re-expanding the same method after a collapse: preserve state.
      const currentMethod = requestAvailabilityFirst
        ? "availability"
        : useShareLink
        ? "share-link"
        : "partner";
      if (value === currentMethod) {
        setExpandedMethod(value);
        return;
      }
      // Switching to a different method: reset in-flight selections so state
      // from the previous method doesn't leak across the flow.
      setExpandedMethod(value);
      setSelectedPartnerId(undefined);
      setSelectedPartnerIds([]);
      setInvitedPerson({ name: "" });
      setGeneratedLink(null);
      setPartnerSearchQuery("");
      setRequestAvailabilityFirst(value === "availability");
      setUseShareLink(value === "share-link");
    };

    return (
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Send className="h-5 w-5 text-white" />
            </div>
            <div>
              <div>Choose How to Send</div>
              <p className="text-sm font-normal text-gray-600 mt-1">
                Pick one option below to expand its details
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Accordion
            type="single"
            collapsible
            value={expandedMethod}
            onValueChange={handleMethodChange}
            className="divide-y"
          >
            {/* Method 1: Request Availability First (Pro/Grow gated) */}
            <AccordionItem value="availability" disabled={!canRequestAvailability} className="border-b-0">
              <AccordionTrigger
                className={`px-6 py-4 hover:no-underline ${
                  !canRequestAvailability ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">Request Availability First</span>
                      <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                        Pro/Grow
                      </Badge>
                    </div>
                    <p className="text-sm font-normal text-gray-600 mt-1">
                      {canRequestAvailability
                        ? "Ask multiple partners if they're available before sharing client details"
                        : "⭐ Pro/Grow feature — Upgrade to unlock"}
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-0">
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-700">
                        Customer details will only be shared after you choose one available partner from those who respond
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="availabilityDescription" className="text-sm font-medium text-gray-700">
                      Short Job Description <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      id="availabilityDescription"
                      value={availabilityDescription}
                      onChange={(e) => setAvailabilityDescription(e.target.value)}
                      placeholder="Briefly describe the job (max 1000 characters)"
                      maxLength={1000}
                      rows={4}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 text-right">
                      {availabilityDescription.length}/1000 characters
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="availabilityWindow" className="text-sm font-medium text-gray-700">
                      Availability Window <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={availabilityWindow}
                      onValueChange={(value: "2" | "24") => setAvailabilityWindow(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select time window" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 Hours</SelectItem>
                        <SelectItem value="24">24 Hours</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Partners have this long to respond before request expires
                    </p>
                  </div>

                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-sm font-medium text-gray-700 pt-3">
                      Select partners to request availability from:
                    </p>
                    {connectionsLoading ? (
                      <div className="text-center py-4 text-gray-500">Loading partners...</div>
                    ) : partnersArray.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No partners yet</p>
                        <p className="text-sm mt-1">Connect with partners in your network first.</p>
                        <Button type="button" variant="outline" className="mt-4" onClick={() => navigate("/network")}>
                          Find Partners
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search by name, company, or trade..."
                            value={partnerSearchQuery}
                            onChange={(e) => setPartnerSearchQuery(e.target.value)}
                            className="pl-9"
                          />
                          {partnerSearchQuery && (
                            <button
                              type="button"
                              onClick={() => setPartnerSearchQuery("")}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-2">
                          {filteredPartnersArray.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                              <p className="font-medium">No partners found</p>
                              <p className="text-sm mt-1">Try a different search term</p>
                            </div>
                          ) : (
                            filteredPartnersArray.map((partner) => (
                              <div
                                key={partner.id}
                                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                                  selectedPartnerIds.includes(partner.id)
                                    ? "bg-blue-50 border border-blue-200"
                                    : "hover:bg-gray-50 border border-transparent cursor-pointer"
                                }`}
                                onClick={(e) => {
                                  if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
                                    return;
                                  }
                                  togglePartnerSelection(partner.id);
                                }}
                              >
                                <Checkbox
                                  checked={selectedPartnerIds.includes(partner.id)}
                                  onCheckedChange={() => togglePartnerSelection(partner.id)}
                                />
                                <Avatar className="w-10 h-10">
                                  <AvatarImage src={partner.profileImageUrl || undefined} alt={`${partner.firstName} ${partner.lastName}`} />
                                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-bold">
                                    {partner.firstName?.[0]}{partner.lastName?.[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">
                                    {partner.firstName} {partner.lastName}
                                  </p>
                                  {partner.company && (
                                    <p className="text-sm text-gray-500">{partner.company}</p>
                                  )}
                                </div>
                                {partner.trade && (
                                  <Badge variant="outline" className="text-xs">{partner.trade}</Badge>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                        {selectedPartnerIds.length > 0 && (
                          <p className="text-sm text-blue-600 mt-2">
                            {selectedPartnerIds.length} partner
                            {selectedPartnerIds.length !== 1 ? "s" : ""} selected
                          </p>
                        )}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                          <p className="text-sm text-amber-700">
                            <strong>Note:</strong> Text invites are not available when requesting availability
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Method 2: Select Partner */}
            <AccordionItem value="partner" className="border-b-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900">Select Partner</div>
                    <p className="text-sm font-normal text-gray-600 mt-1">
                      Send directly to one partner from your network
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-0">
                {connectionsLoading ? (
                  <div className="text-center py-4 text-gray-500">Loading partners...</div>
                ) : partnersArray.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No partners yet</p>
                    <p className="text-sm mt-1">Connect with partners in your network first.</p>
                    <Button type="button" variant="outline" className="mt-4" onClick={() => navigate("/network")}>
                      Find Partners
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Select a partner to send this referral to:
                    </p>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search by name, company, or trade..."
                        value={partnerSearchQuery}
                        onChange={(e) => setPartnerSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                      {partnerSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setPartnerSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-2">
                      {filteredPartnersArray.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="font-medium">No partners found</p>
                          <p className="text-sm mt-1">Try a different search term</p>
                        </div>
                      ) : (
                        filteredPartnersArray.map((partner) => {
                          const isSelected = selectedPartnerId === partner.id;
                          return (
                            <div
                              key={partner.id}
                              role="button"
                              tabIndex={0}
                              aria-pressed={isSelected}
                              onClick={() => setSelectedPartnerId(partner.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setSelectedPartnerId(partner.id);
                                }
                              }}
                              className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                                isSelected
                                  ? "bg-blue-50 border border-blue-200"
                                  : "hover:bg-gray-50 border border-transparent"
                              }`}
                            >
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={partner.profileImageUrl || undefined} alt={`${partner.firstName} ${partner.lastName}`} />
                                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-bold">
                                  {partner.firstName?.[0]}{partner.lastName?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {partner.firstName} {partner.lastName}
                                </p>
                                {partner.company && (
                                  <p className="text-sm text-gray-500">{partner.company}</p>
                                )}
                              </div>
                              {partner.trade && (
                                <Badge variant="outline" className="text-xs">{partner.trade}</Badge>
                              )}
                              {isSelected && (
                                <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Method 3: Invite Partner via Text (share link) */}
            <AccordionItem value="share-link" className="border-b-0">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Share2 className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900">Invite Partner via Text</div>
                    <p className="text-sm font-normal text-gray-600 mt-1">
                      Generate a share link for someone not yet in your network
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-0">
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <LinkIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="font-medium text-blue-900">Share Link Mode</h3>
                        <p className="text-sm text-blue-700 mt-1">
                          Generate a link to invite someone not yet in your network. They can click it, sign up, and claim this referral.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Invited Person Details (Optional)</h4>
                    <div className="grid grid-cols-1 gap-3">
                      <Input
                        value={invitedPerson.name}
                        onChange={(e) => setInvitedPerson(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Their name (e.g., John Smith)"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900 mb-1">Next Step</p>
                        <p className="text-sm text-blue-700">
                          Click "Continue" to review your referral. You'll be able to generate and share the invitation link on the next screen.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    );
  };

  // Step 2: Client Information
  const renderStep2 = () => {
    // Email and phone are optional; only flag them as invalid when a value is present
    const emailInvalid =
      formData.clientEmail.trim() !== "" && !isValidEmail(formData.clientEmail.trim());
    const phoneInvalid =
      formData.clientPhone.trim() !== "" && !isValidPhone(formData.clientPhone);

    return (
    <Card>
      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <User className="h-5 w-5 text-white" />
          </div>
          <div>
            <div>Client Information</div>
            <p className="text-sm font-normal text-gray-600 mt-1">
              Enter the client's contact details
            </p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Client Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Client Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.clientName}
            onChange={(e) => handleInputChange("clientName", e.target.value)}
            placeholder="John Smith"
            className="w-full"
            autoFocus
          />
        </div>

        {/* Client Email */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            Client Email
          </label>
          <Input
            type="email"
            value={formData.clientEmail}
            onChange={(e) => handleInputChange("clientEmail", e.target.value)}
            placeholder="john@example.com"
            className={`w-full ${emailInvalid ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            aria-invalid={emailInvalid}
          />
          {emailInvalid && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Please enter a valid email address.
            </p>
          )}
        </div>

        {/* Client Phone */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            Client Phone
          </label>
          <Input
            type="tel"
            value={formData.clientPhone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="(555) 123-4567"
            className={`w-full ${phoneInvalid ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            maxLength={14}
            aria-invalid={phoneInvalid}
          />
          {phoneInvalid && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Please enter a valid 10-digit phone number.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
    );
  };

  // Step 3: Job Details
  const renderStep3 = () => (
    <Card>
      <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 border-b">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <div>Job Details</div>
            <p className="text-sm font-normal text-gray-600 mt-1">
              Describe the work and location
            </p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Job Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Job Description <span className="text-red-500">*</span>
          </label>
          <Textarea
            value={formData.jobDescription}
            onChange={(e) => handleInputChange("jobDescription", e.target.value)}
            placeholder="Describe the job or project... e.g., Kitchen remodel - needs new plumbing for sink relocation"
            className="resize-none overflow-y-auto"
            rows={2}
            autoFocus
          />
        </div>

        {/* Trade Needed Single-Select Dropdown */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Trade Needed
          </label>
          <p className="text-xs text-gray-500">
            Select the trade this job relates to
          </p>
          <Popover open={tradePopoverOpen} onOpenChange={setTradePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between h-auto min-h-[40px] text-left font-normal"
              >
                <span className={`text-sm ${selectedTrade || (showCustomTradeInput && customTrade.trim()) ? 'text-gray-900' : 'text-gray-500'}`}>
                  {selectedTrade
                    ? selectedTrade
                    : showCustomTradeInput && customTrade.trim()
                      ? customTrade.trim()
                      : showCustomTradeInput
                        ? 'Other (enter below)'
                        : 'Select a trade...'}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command
                filter={(value, search) => {
                  if (!search) return 1;
                  return value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0;
                }}
              >
                <CommandInput placeholder="Search trades..." className="h-9" />
                <CommandList className="max-h-64">
                  <CommandEmpty>No trade found.</CommandEmpty>
                  {/* Other/Custom option at the top */}
                  <CommandItem
                    key="__CUSTOM__"
                    value="Other Custom"
                    onSelect={() => selectTrade('__CUSTOM__')}
                    className="border-b border-gray-100"
                  >
                    <span className="flex-1 font-medium text-purple-700">Other</span>
                    {showCustomTradeInput && (
                      <Check className="ml-2 h-4 w-4 text-purple-600" />
                    )}
                  </CommandItem>
                  {tradesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="text-sm text-gray-500">Loading trades...</span>
                    </div>
                  ) : (
                    tradeSections.map((section) => (
                      <CommandGroup key={section.section || 'Other'} heading={section.section || 'Other'}>
                        {section.trades.map((trade) => (
                          <CommandItem
                            key={trade.id}
                            value={`${trade.name} ${trade.section || ''}`}
                            onSelect={() => selectTrade(trade.name)}
                          >
                            <span className="flex-1">{trade.name}</span>
                            {selectedTrade === trade.name && (
                              <Check className="ml-2 h-4 w-4 text-blue-600" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Custom Trade Input (single) */}
          {showCustomTradeInput && (
            <div className="space-y-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-xs text-purple-700 font-medium">Enter your custom job type:</p>
              <div className="flex items-center gap-2">
                <Input
                  value={customTrade}
                  onChange={(e) => setCustomTrade(e.target.value)}
                  placeholder="Custom job type"
                  className="flex-1 bg-white"
                  maxLength={100}
                />
                <button
                  type="button"
                  onClick={clearCustomTrade}
                  className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                  aria-label="Clear custom trade"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Selected trade badge */}
          {(selectedTrade || (showCustomTradeInput && customTrade.trim())) && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedTrade && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                >
                  {selectedTrade}
                  <button
                    type="button"
                    onClick={clearTradeSelection}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {showCustomTradeInput && customTrade.trim() && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full"
                >
                  {customTrade.trim()}
                  <button
                    type="button"
                    onClick={() => setCustomTrade('')}
                    className="hover:bg-purple-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Urgency Level Dropdown */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            Urgency Level
          </label>
          <p className="text-xs text-gray-500">
            Let your partner know how time-sensitive this job is
          </p>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between h-10 text-left font-normal"
              >
                <span className="flex items-center gap-2">
                  {urgencyLevel === 'low' && <Clock className="w-4 h-4 text-gray-600" />}
                  {urgencyLevel === 'normal' && <Clock className="w-4 h-4 text-blue-600" />}
                  {urgencyLevel === 'urgent' && <Zap className="w-4 h-4 text-red-600" />}
                  <span className="text-sm">
                    {urgencyLevel === 'low' && 'Low'}
                    {urgencyLevel === 'normal' && 'Normal'}
                    {urgencyLevel === 'urgent' && 'Urgent'}
                    {!urgencyLevel && <span className="text-gray-500">Select urgency level...</span>}
                  </span>
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search urgency level..." className="h-9" />
                <CommandEmpty>No urgency level found.</CommandEmpty>
                <CommandGroup className="max-h-64 overflow-auto">
                  <CommandItem
                    value="low"
                    onSelect={() => setUrgencyLevel('low')}
                  >
                    <Clock className="w-4 h-4 text-gray-600 mr-2" />
                    <span className="flex-1">Low</span>
                    {urgencyLevel === 'low' && (
                      <Check className="ml-2 h-4 w-4 text-gray-600" />
                    )}
                  </CommandItem>
                  <CommandItem
                    value="normal"
                    onSelect={() => setUrgencyLevel('normal')}
                  >
                    <Clock className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="flex-1">Normal</span>
                    {urgencyLevel === 'normal' && (
                      <Check className="ml-2 h-4 w-4 text-blue-600" />
                    )}
                  </CommandItem>
                  <CommandItem
                    value="urgent"
                    onSelect={() => setUrgencyLevel('urgent')}
                  >
                    <Zap className="w-4 h-4 text-red-600 mr-2" />
                    <span className="flex-1">Urgent</span>
                    {urgencyLevel === 'urgent' && (
                      <Check className="ml-2 h-4 w-4 text-red-600" />
                    )}
                  </CommandItem>
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Street Address */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            Street Address <span className="text-gray-500 text-xs">(Optional)</span>
          </label>
          <StreetAddressAutocomplete
            value={formData.streetAddress}
            onChange={(value) => handleInputChange("streetAddress", value)}
            onSelect={(address: SelectedAddress) => {
              // A picked address fills City, State too — when our city list knows the city.
              // Otherwise only the street is filled and the user picks the city as usual.
              if (!address.location) return;
              setSelectedLocation({ city: address.location.city, state: address.location.stateCode });
              setFormData((prev) => ({ ...prev, cityState: address.location!.formatted }));
              setCityStateInput("");
            }}
            placeholder="123 Main Street"
            className="w-full"
          />
        </div>

        {/* City, State */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            City, State <span className="text-red-500">*</span>
          </label>
          <CityStateAutocomplete
            value={cityStateInput}
            onChange={setCityStateInput}
            onSelect={(location: LocationResult) => {
              const newLocation = {
                city: location.city,
                state: location.stateCode,
              };
              setSelectedLocation(newLocation);
              setFormData((prev) => ({
                ...prev,
                cityState: location.formatted,
              }));
              setCityStateInput("");
            }}
            placeholder="Start typing a city..."
            className="w-full"
            disabled={selectedLocation !== null}
          />

          {/* Location Badge */}
          {selectedLocation && (
            <div className="flex flex-wrap gap-2 pt-2">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full">
                <MapPin className="w-3 h-3" />
                {selectedLocation.state ? `${selectedLocation.city}, ${selectedLocation.state}` : selectedLocation.city}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLocation(null);
                    setFormData((prev) => ({
                      ...prev,
                      cityState: "",
                    }));
                  }}
                  className="hover:bg-indigo-200 rounded-full p-0.5 ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}
        </div>

        {/* File Attachments */}
        <div className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-gray-400" />
              Attachments
            </label>
            <p className="text-xs text-gray-400">Images & videos only</p>
          </div>

          {/* One tile per file, shown as soon as it is picked; each uploads in the background */}
          {uploads.items.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3">
              {uploads.items.map((item) => (
                <div
                  key={item.id}
                  data-testid="attachment-tile"
                  data-status={item.status}
                  className={cn(
                    "relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 border group",
                    item.status === 'failed' ? "border-red-400" : "border-gray-200"
                  )}
                >
                  {/* Preview */}
                  {item.previewUrl ? (
                    <img src={item.previewUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {item.kind === 'video' ? (
                        <Video className="w-6 h-6 text-gray-400" />
                      ) : (
                        <Paperclip className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  )}

                  {/* Upload progress */}
                  {item.status === 'uploading' && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      {item.progress === null ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      ) : (
                        <span className="text-white text-xs font-semibold">{Math.round(item.progress * 100)}%</span>
                      )}
                    </div>
                  )}

                  {/* Failed: tap to retry (the reason is listed below the grid) */}
                  {item.status === 'failed' && (
                    <button
                      type="button"
                      onClick={() => void uploads.retry(item.id)}
                      aria-label={`Retry ${item.name}`}
                      className="absolute inset-0 bg-red-900/60 flex flex-col items-center justify-center gap-0.5 text-white"
                    >
                      <RotateCw className="w-5 h-5" />
                      <span className="text-[10px] font-semibold">Retry</span>
                    </button>
                  )}

                  {/* File name overlay, with the progress bar on top of it while uploading */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/40">
                    {item.status === 'uploading' && (
                      <div className="h-1 bg-white/30">
                        <div
                          className="h-full bg-white transition-[width] duration-200"
                          style={{ width: `${Math.round((item.progress ?? 0) * 100)}%` }}
                        />
                      </div>
                    )}
                    <p className="text-white text-[10px] truncate px-1.5 py-1">{item.name}</p>
                  </div>

                  {/* Remove (cancels an upload in progress). Always visible on touch screens. */}
                  <button
                    type="button"
                    onClick={() => uploads.remove(item.id)}
                    aria-label={`Remove ${item.name}`}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Why each failed file failed, with Retry / Remove */}
          {uploads.summary.failed > 0 && (
            <div className="space-y-2 mb-3">
              {uploads.items
                .filter((item) => item.status === 'failed')
                .map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm"
                  >
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="flex-1 min-w-0 text-red-700 break-words">{item.error}</p>
                    <div className="flex gap-1 shrink-0">
                      <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={() => void uploads.retry(item.id)}>
                        Retry
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => uploads.remove(item.id)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Upload button — always visible */}
          <input
            ref={fileInputRef}
            type="file"
            accept={MEDIA_ACCEPT_ATTRIBUTE}
            multiple
            className="hidden"
            onChange={handleFileUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploads.items.length >= 20}
            className="w-full bg-white border border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center gap-2 text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50"
          >
            {uploads.items.length >= 20 ? (
              <>
                <ImagePlus className="w-4 h-4" />
                <span className="text-sm">Maximum files reached (20)</span>
              </>
            ) : (
              <>
                <ImagePlus className="w-4 h-4" />
                <span className="text-sm">Add photos or videos</span>
              </>
            )}
          </button>
          {/* Native camera (Card 20 task B). The picker above stays the way to attach
              videos and multiple files — @capacitor/camera returns exactly one image —
              so this is an additional action, not a replacement. Hidden on web, where
              the file input's own "Take Photo" already reaches the camera. */}
          {isNativeCaptureAvailable() && (
            <button
              type="button"
              onClick={() =>
                void captureImageInto(
                  (file) => uploadSelectedFiles([file]),
                  showError,
                  { source: 'camera' },
                )
              }
              disabled={uploads.items.length >= 20}
              className="w-full mt-2 bg-white border border-gray-300 rounded-xl p-4 flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Camera className="w-4 h-4" />
              <span className="text-sm">Take a photo</span>
            </button>
          )}
          <p className="text-xs text-gray-400 mt-1.5">Images: Max 25MB | Videos: Max 250MB | Max 20 files ({uploads.items.length}/20)</p>
          <p className="text-xs text-gray-400 mt-0.5">Max video size: 250MB. This is usually 2–3 minutes on standard phone settings, less if recording 4K/60fps.</p>
        </div>
      </CardContent>
    </Card>
  );

  // Step 4: Review & Send
  const renderStep4 = () => {
    const partnerCount = sendToMultiple ? selectedPartnerIds.length : 1;
    const partners = sendToMultiple ? selectedPartners : (selectedPartner ? [selectedPartner] : []);
    const fullAddress = formData.streetAddress.trim()
      ? `${formData.streetAddress}, ${formData.cityState}`
      : formData.cityState;

    return (
      <Card>
        <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-white" />
            </div>
            <div>
              <div>Review Your Referral</div>
              <p className="text-sm font-normal text-gray-600 mt-1">
                {useShareLink
                  ? "Confirm the details and finalize your share link"
                  : "Confirm the details before sending"}
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Sending To / Share Link */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {useShareLink ? "Share Link Method" : "Sending To"}
            </h3>
            {useShareLink ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Share2 className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900">
                      {generatedLink ? 'Invitation Link Created' : 'Share Link Method'}
                    </p>
                    {invitedPerson.name && (
                      <p className="text-sm text-blue-700 mt-1">
                        For: {invitedPerson.name}
                      </p>
                    )}
                    {!generatedLink && (
                      <p className="text-sm text-blue-700 mt-1">
                        Click "Create & Share Link" below to generate your invitation link
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {partners.map((partner) => (
                <div
                  key={partner.id}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={partner.profileImageUrl || undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs">
                      {partner.firstName?.[0]}{partner.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-gray-900">
                    {partner.firstName} {partner.lastName}
                  </span>
                  {partner.trade && (
                    <Badge variant="outline" className="text-xs">
                      {partner.trade}
                    </Badge>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>

          {/* Client Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Client Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{formData.clientName}</span>
              </div>
              {formData.clientEmail && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>{formData.clientEmail}</span>
                </div>
              )}
              {formData.clientPhone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{formData.clientPhone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Job Details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Job Details
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-900">{formData.jobDescription}</p>
              </div>
              {(selectedTrade || (showCustomTradeInput && customTrade.trim())) && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Trade Needed</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedTrade && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                      >
                        {selectedTrade}
                      </Badge>
                    )}
                    {showCustomTradeInput && customTrade.trim() && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-purple-50 text-purple-700 border-purple-200"
                      >
                        {customTrade.trim()}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              {/* Urgency Level */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Urgency</p>
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    urgencyLevel === 'urgent' ? 'bg-red-50 text-red-700 border-red-200' :
                    urgencyLevel === 'normal' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  }`}
                >
                  {urgencyLevel === 'urgent' && <Zap className="w-3 h-3 mr-1" />}
                  {(urgencyLevel === 'normal' || urgencyLevel === 'low') && <Clock className="w-3 h-3 mr-1" />}
                  {urgencyLevel.charAt(0).toUpperCase() + urgencyLevel.slice(1)}
                </Badge>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <span className="text-gray-900">{fullAddress}</span>
              </div>
              {uploads.items.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {uploads.items.map((item) => (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center gap-1 px-2 py-1 bg-white rounded border text-sm",
                          item.status === 'failed' && "border-red-300 text-red-700"
                        )}
                      >
                        {item.status === 'uploading' ? (
                          <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />
                        ) : item.status === 'failed' ? (
                          <AlertCircle className="w-3 h-3 text-red-500" />
                        ) : (
                          <Paperclip className="w-3 h-3 text-gray-400" />
                        )}
                        <span className="truncate max-w-[150px]">{item.name}</span>
                        {item.status === 'uploading' && item.progress !== null && (
                          <span className="text-xs text-gray-400">{Math.round(item.progress * 100)}%</span>
                        )}
                        {item.status === 'failed' && <span className="text-xs">Failed</span>}
                      </div>
                    ))}
                  </div>
                  {uploads.summary.failed > 0 && (
                    <p className="text-xs text-red-600 mt-2">
                      Some files didn't upload. Go back to Job Details to retry or remove them.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Share Link Actions (only if using share link) */}
          {useShareLink && generatedLink && (
            <div className="space-y-3 pt-3 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Share Your Invitation Link
              </h3>

              {/* Mobile Share Button (Primary Action) */}
              <Button
                type="button"
                onClick={() => handleNativeShare()}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share Link
              </Button>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-green-700 font-medium mb-2">Or copy manually:</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border border-green-300 rounded px-3 py-2 text-sm text-gray-700 break-all">
                    {generatedLink}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Share2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">How to share:</p>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• <strong>Mobile:</strong> Tap "Share Link" to choose SMS, WhatsApp, email, etc.</li>
                      <li>• <strong>Desktop:</strong> Click "Copy" and paste into your messaging app</li>
                      <li>• When they click the link and sign up, they can claim this referral</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Consent Checkbox - Required before sending */}
          <div className="border-t pt-4 mt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasCustomerConsent}
                onChange={(e) => setHasCustomerConsent(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I confirm I have the customer's consent to share their information for this referral.
              </span>
            </label>
          </div>

          {/* Upgrade prompt if at limit */}
          {isAtLimit('monthlyReferrals') && (
            <UpgradePrompt
              reason="limit-reached"
              limitType="monthlyReferrals"
              variant="banner"
            />
          )}
        </CardContent>
      </Card>
    );
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep2(); // Client Info
      case 2:
        return renderStep3(); // Job Details
      case 3:
        return renderStep1(); // Partner Selection
      case 4:
        return renderStep4(); // Review
      default:
        return null;
    }
  };

  return (
    <AppLayout activeTab="referrals" title="Send Referral">
      <div ref={wizardTopRef} className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pt-[5px] md:pt-0">
        {/* Step Indicator */}
        <StepIndicator />

        {/* Step Content */}
        <div className="mb-6">{renderStepContent()}</div>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {useShareLink && generatedLink ? (
            /* Show single Close button after invitation link is created */
            <Button
              type="button"
              onClick={() => navigate(isOrgMember ? '/organization' : '/dashboard')}
              className="flex-1 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-lg font-semibold"
            >
              Close
            </Button>
          ) : (
            <>
              {/* Back Button */}
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousStep}
                  className={
                    currentStep === STEPS.length
                      ? "flex-1 h-12 text-lg font-semibold"
                      : "flex-1"
                  }
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}

              {/* Cancel Button (only on step 1) */}
              {currentStep === 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              )}

              {/* Next / Submit Button */}
              {currentStep < STEPS.length ? (
                <Button
                  type="button"
                  onClick={goToNextStep}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmitClick}
                  disabled={
                    submitWhenUploadsFinish ||
                    createReferralMutation.isPending ||
                    isGeneratingLink ||
                    !canPerformAction('monthlyReferrals') ||
                    !hasCustomerConsent // Require consent checkbox
                  }
                  className={cn(
                    "flex-1 h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-lg font-semibold",
                    // The longer progress label wraps to two lines at phone width.
                    submitWhenUploadsFinish && "text-sm leading-tight whitespace-normal"
                  )}
                >
                  {submitWhenUploadsFinish ? (
                    <>
                      {/* No room for the spinner beside the label at phone width. */}
                      <div className="hidden sm:block animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2 shrink-0" />
                      <span>
                        Finishing uploads{" "}
                        <span className="whitespace-nowrap">({uploads.summary.done} of {uploads.summary.total})...</span>
                      </span>
                    </>
                  ) : (createReferralMutation.isPending || isGeneratingLink) ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                      {useShareLink ? "Creating Link..." : requestAvailabilityFirst ? "Sending Request..." : "Sending..."}
                    </>
                  ) : useShareLink ? (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Create & Share Link
                    </>
                  ) : requestAvailabilityFirst || selectedPartnerIds.length > 1 ? (
                    <>
                      <Clock className="w-5 h-5 mr-2" />
                      Send Availability Request
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Referral
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
